/**
 * The three-way "what cut is this" choice at add time (Bobbie's stated ASAP
 * #1, 2026-08-07), replacing the old "What is this for? Classroom" category
 * dropdown plus a separate final-cut checkbox on the class page's Music and
 * Video add form (MediaPanel.tsx). One tap answers both what the track is
 * FOR and whether it is performance ready - the two used to be two separate
 * controls that could disagree (a "Recital dance" pick with the final-cut box
 * left unchecked).
 *
 * Pure and Firebase-free, same posture as musicDownload.ts: only TYPES come
 * from offerings.ts (`import type`, erased before Node or the bundler ever
 * resolve the module), so this file never pulls in firebase/* and is safe to
 * exercise directly under plain Node.
 */
import type { MediaCategory, MediaLink } from './offerings';

/** The choices a teacher, Director, or Jr assistant taps when adding
 * a track. Named for what they MEAN to a person, not the stored shape.
 * 'eval' joined 2026-09-01 for the comp dance / comp class evaluation
 * video (Bobbie: "a video upload specifically for eval. so one of the
 * drop downs for videos") - staff only, never a final cut, never shown to
 * a family. See familyVisibleMediaLinks below. */
export type CutChoice = 'comp' | 'recital' | 'class' | 'eval';

/** Display order for the pill buttons - comp first, classroom last, eval
 * last of all since it is the least common pick, matching how Bobbie wrote
 * the spec out herself for the original three. */
export const CUT_CHOICE_ORDER: readonly CutChoice[] = ['comp', 'recital', 'class', 'eval'];

export const CUT_CHOICE_LABELS: Record<CutChoice, string> = {
  comp: 'Final comp cut',
  recital: 'Final recital cut',
  class: 'In class only',
  eval: 'Eval video (staff only)',
};

/** "In class only" is the safe default - nothing reads as performance ready
 * until a person deliberately taps one of the other two. */
export const DEFAULT_CUT_CHOICE: CutChoice = 'class';

/**
 * Which CutChoice pills a media add form should offer for one kind
 * ('music' or 'video'). Eval is video-only - an eval MUSIC link has
 * nowhere to land on the Production Tracker's music sweep (matchSlotMusic
 * treats an unlabeled/uncategorized track as a candidate everywhere), and
 * Bobbie's ask was specifically "a video upload specifically for eval".
 *
 * Shared by MediaPanel.tsx (the class page's Videos tab) and the legacy
 * Offering.tsx class editor's own add form (review finding, 2026-09-01:
 * Offering.tsx had its own unfiltered CUT_CHOICE_ORDER map, which showed
 * the eval pill on music adds too) so the two add surfaces cannot drift on
 * this again.
 */
export function cutChoicesForKind(kind: 'music' | 'video'): readonly CutChoice[] {
  return kind === 'video' ? CUT_CHOICE_ORDER : CUT_CHOICE_ORDER.filter((choice) => choice !== 'eval');
}

/**
 * A short-lived slot held while a class media file is uploading. It is stored
 * in the same bounded array so concurrent uploads cannot both claim item 200.
 * Family and production surfaces must never render or count this internal row.
 */
export const MEDIA_UPLOAD_PENDING_PATH = '/media-upload-pending';

export function isMediaUploadReservation(link: Pick<MediaLink, 'url'>): boolean {
  try {
    return new URL(link.url).pathname === MEDIA_UPLOAD_PENDING_PATH;
  } catch {
    return false;
  }
}

/** Only completed media belongs on family and production surfaces. */
export function publishedMediaLinks(links: readonly MediaLink[]): MediaLink[] {
  return links.filter((link) => !isMediaUploadReservation(link));
}

/**
 * publishedMediaLinks, minus category 'eval'. THE one filter every family,
 * dancer, comp-board, and public surface must route through - eval videos
 * are a staff-only tool (Bobbie's ruling, 2026-09-01) and this is the
 * single place that boundary is defined, so every reader draws the same
 * line. Data-layer readability is NOT changed by this: offerings docs
 * (videoLinks included) stay family-readable at the Firestore level per
 * shared/firestore.rules, same as every other media link - this only keeps
 * an eval link off what a family or the public web is ever SHOWN.
 */
export function familyVisibleMediaLinks(links: readonly MediaLink[]): MediaLink[] {
  return publishedMediaLinks(links).filter((link) => link.category !== 'eval');
}

/** Is this one link an eval upload? Used for the "skip the family
 * announcement" branch at add time and the tracker's eval-video-posted
 * marker - kept as one predicate so both stay in sync with the category. */
export function isEvalMediaLink(link: Pick<MediaLink, 'category'>): boolean {
  return link.category === 'eval';
}

/** Does this offering carry at least one eval video? Pure helper for the
 * Comp/Recital Tracker's Evals report row marker. */
export function hasEvalVideo(videoLinks: readonly MediaLink[] | undefined | null): boolean {
  return (videoLinks ?? []).some(isEvalMediaLink);
}

/**
 * The exact mapping Bobbie specified: picking a final cut sets BOTH the
 * category and the final-cut flag in one tap. "In class only" and "Eval
 * video" are the only choices that can never be a final cut - matches the
 * existing MediaLink doc comment ("classroom music is never asked") and
 * earnedCutLabel below, which a family never earns from an eval pick
 * either way.
 */
export function cutChoiceToMedia(choice: CutChoice): { category: MediaCategory; finalCut: boolean } {
  if (choice === 'comp') return { category: 'comp', finalCut: true };
  if (choice === 'recital') return { category: 'recital', finalCut: true };
  if (choice === 'eval') return { category: 'eval', finalCut: false };
  return { category: 'class', finalCut: false };
}

/**
 * "<Song title> - <cut label>", e.g. "Thunderstruck - Final comp cut". Empty
 * when songTitle is blank, so the caller falls back to suggestedMediaLabel
 * (MediaPanel.tsx's existing real-name behavior, task #88) - never the
 * camera or file's own raw name either way.
 */
export function cutChoiceLabelForKind(choice: CutChoice, kind: 'music' | 'video'): string {
  return kind === 'music' ? CUT_CHOICE_LABELS[choice].replace(/^Final /, '') : CUT_CHOICE_LABELS[choice];
}

export function generatedMediaLabel(
  songTitle: string,
  choice: CutChoice,
  kind: 'music' | 'video' = 'video',
  artist = ''
): string {
  const title = songTitle.trim();
  if (!title) return '';
  // "<Song> by <Artist> - <cut>" only once an artist is typed; a blank artist
  // leaves every generated label exactly as it was before the field existed.
  const who = artist.trim().slice(0, 240);
  return `${who ? `${title} by ${who}` : title} - ${cutChoiceLabelForKind(choice, kind)}`;
}

/**
 * The label a FAMILY viewer earns the right to see: the cut-specific pill
 * text once a track is marked the final cut, or nothing at all. Null on
 * anything not yet final - a draft recital or comp track, or "In class
 * only" media - so a family never sees a "needs" or "not final" nag, only
 * what the studio has actually finished (Bobbie, 2026-08-07: parents see
 * only the earned labels, never "needs"). Staff render the amber not-final
 * state themselves when this returns null; that gate lives in MediaPanel.tsx,
 * not here, since it depends on who is looking.
 */
export function earnedCutLabel(
  category: MediaCategory | null | undefined,
  finalCut: boolean | null | undefined
): string | null {
  if (!finalCut) return null;
  if (category === 'comp') return CUT_CHOICE_LABELS.comp;
  if (category === 'recital') return CUT_CHOICE_LABELS.recital;
  return null;
}

/** Which media a Director's end-of-season clear-out removes. */
export type SeasonWipeScope = 'classOnly' | 'everything';

/**
 * Keeps only the links that SURVIVE the chosen wipe - the pure filter behind
 * Studio Settings' "End of season clear-out" card.
 *
 * 'classOnly' removes exactly category 'class', the literal "In class only"
 * tag, and nothing else. It deliberately leaves a recital or comp track
 * alone even when it was never marked final: an unfinished final cut is
 * still a real, in-progress performance track, not classroom clutter, and
 * wiping it would delete work nobody asked to lose. It also leaves an
 * uncategorized/unlabeled upload alone - the Music job's own "needs a
 * label" treatment of these same links (productionTracker/helpers.ts) never
 * assumes an unlabeled track is classroom-only, and this filter makes the
 * same choice for the same reason: guessing and deleting is worse than
 * leaving it for a person to label.
 *
 * 'everything' keeps nothing, including a marked final cut - Bobbie's own
 * second option, implemented literally so nobody "fixes" this into a safer
 * default later.
 */
export function keepAfterSeasonWipe(
  links: readonly MediaLink[],
  scope: SeasonWipeScope
): MediaLink[] {
  if (scope === 'everything') return [];
  return links.filter((link) => link.category !== 'class');
}
